import React, { useState, useEffect } from 'react';
import { DashboardContentShell, KPIBar, DashboardSection, DashboardWidget } from '../shared/layout/DashboardLayout';
import { MetricCard } from '../shared/layout/CardLayout';
import { AlertList } from '../shared/alerts/AlertList';
import { AlertActions } from '../shared/alerts/AlertActions';
import { AIExplanationPanel } from '../shared/ai/AIExplanationPanel';
import { useAlertSubscription } from '../shared/hooks/useAlertSubscription';
import { useTelemetrySubscription } from '../shared/hooks/useTelemetrySubscription';
import { useRealTimeData } from '../shared/hooks/useRealTimeData';
import { Alert, Machine, TelemetrySample } from '@cbm/shared';

type MachineStatus = 'healthy' | 'warning' | 'critical' | 'offline';

function MachineStatusCard({ 
  machine, 
  status, 
  activeAlerts, 
  latestTelemetry,
  onViewDetails 
}: {
  machine: Machine;
  status: MachineStatus;
  activeAlerts: Alert[];
  latestTelemetry?: TelemetrySample;
  onViewDetails: (machineId: string) => void;
}) {
  const getStatusColor = (status: MachineStatus) => {
    switch (status) {
      case 'healthy': return 'bg-emerald-500';
      case 'warning': return 'bg-amber-500';
      case 'critical': return 'bg-red-500';
      case 'offline': return 'bg-slate-500';
      default: return 'bg-slate-500';
    }
  };

  const criticalAlerts = activeAlerts.filter(a => a.severity === 'LATE').length;
  const warningAlerts = activeAlerts.filter(a => a.severity === 'MID' || a.severity === 'EARLY').length;

  return (
    <div 
      className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors cursor-pointer"
      onClick={() => onViewDetails(machine.sk.split('#')[1])}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">{machine.displayName}</h3>
        <div className={`w-4 h-4 rounded-full ${getStatusColor(status)}`} />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-400">Status:</span>
          <span className={`text-sm font-medium ${
            status === 'healthy' ? 'text-emerald-400' :
            status === 'warning' ? 'text-amber-400' :
            status === 'critical' ? 'text-red-400' :
            'text-slate-400'
          }`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-400">Profile:</span>
          <span className="text-sm font-medium text-white capitalize">
            {machine.usageProfile.replace('_', ' ')}
          </span>
        </div>
        
        {activeAlerts.length > 0 && (
          <div className="pt-2 border-t border-slate-700">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Active Alerts:</span>
              <div className="flex space-x-2">
                {criticalAlerts > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                    {criticalAlerts} Critical
                  </span>
                )}
                {warningAlerts > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                    {warningAlerts} Warning
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {latestTelemetry && (
          <div className="pt-2 border-t border-slate-700">
            <span className="text-xs text-slate-500">
              Last update: {new Date(latestTelemetry.timestamp).toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function PlantOperatorDashboard() {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<string | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(true);
  
  const { alerts } = useAlertSubscription();
  const { samples, subscribe, isConnected: wsConnected } = useTelemetrySubscription();
  const { isConnected } = useRealTimeData(null as any, () => true as any, () => true as any, () => true as any);

  useEffect(() => {
    const loadMachines = async () => {
      const mockMachines: Machine[] = [
        {
          pk: 'TENANT#demo',
          sk: 'MACHINE#M-001',
          displayName: 'Production Line A - Station 1',
          designVariant: 'Model X-200',
          usageProfile: 'normal',
          createdAt: new Date().toISOString(),
        },
        {
          pk: 'TENANT#demo',
          sk: 'MACHINE#M-002',
          displayName: 'Production Line A - Station 2',
          designVariant: 'Model X-200',
          usageProfile: 'aggressive',
          createdAt: new Date().toISOString(),
        },
        {
          pk: 'TENANT#demo',
          sk: 'MACHINE#M-003',
          displayName: 'Production Line B - Station 1',
          designVariant: 'Model Y-150',
          usageProfile: 'poor_pm',
          createdAt: new Date().toISOString(),
        }
      ];
      setMachines(mockMachines);
      setLoading(false);
      
      // Subscribe to all machines for telemetry
      mockMachines.forEach(machine => {
        const machineId = machine.sk.split('#')[1];
        console.log('Subscribing to machine:', machineId);
        subscribe(machineId);
      });
    };

    loadMachines();
  }, [subscribe]);

  const getMachineStatus = (machineId: string): MachineStatus => {
    const machineAlerts = alerts.filter(alert => 
      alert.machineId === machineId && alert.status !== 'CLOSED'
    );
    
    if (machineAlerts.some(alert => alert.severity === 'LATE')) return 'critical';
    if (machineAlerts.some(alert => alert.severity === 'MID')) return 'warning';
    if (machineAlerts.some(alert => alert.severity === 'EARLY')) return 'warning';
    
    const machineData = (samples as any)[machineId];
    if (machineData && machineData.length > 0) {
      const latestSample = machineData[machineData.length - 1];
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      if (new Date(latestSample.timestamp).getTime() < fiveMinutesAgo) {
        return 'offline';
      }
    }
    
    return 'healthy';
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    console.log('Acknowledging alert:', alertId);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      
      const response = await fetch(`${apiUrl}/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('✅ Alert acknowledged successfully:', alertId);
        // Reload to root to avoid S3 access denied on CloudFront
        window.location.href = window.location.origin + window.location.pathname;
      } else {
        console.error('❌ Failed to acknowledge alert:', await response.text());
      }
    } catch (error) {
      console.error('❌ Error acknowledging alert:', error);
    }
  };

  const handleViewMachineDetails = (machineId: string) => {
    setSelectedMachine(selectedMachine === machineId ? null : machineId);
  };

  // Debug logging
  console.log('🔍 PlantOperatorDashboard Debug:');
  console.log('  Total alerts:', alerts.length);
  console.log('  All alerts:', alerts);
  
  const plantAlerts = alerts.filter(alert => alert.owner === 'PLANT');
  console.log('  Plant alerts (owner=PLANT):', plantAlerts.length, plantAlerts);
  
  const activeAlerts = plantAlerts.filter(alert => alert.status !== 'CLOSED');
  console.log('  Active alerts (not CLOSED):', activeAlerts.length, activeAlerts);
  
  const openAlerts = activeAlerts.filter(a => a.status === 'OPEN').length;
  const ackAlerts = activeAlerts.filter(a => a.status === 'ACKNOWLEDGED').length;
  const criticalAlerts = activeAlerts.filter(a => a.severity === 'LATE').length;
  
  console.log('  Open:', openAlerts, 'Ack:', ackAlerts, 'Critical:', criticalAlerts);

  if (loading) {
    return (
      <DashboardContentShell>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardContentShell>
    );
  }

  return (
    <DashboardContentShell>
      <KPIBar
        metrics={[
          { label: 'Machines', value: machines.length, status: 'good' },
          { label: 'Open Alerts', value: openAlerts, status: openAlerts > 5 ? 'critical' : openAlerts > 2 ? 'warning' : 'good' },
          { label: 'Acknowledged', value: ackAlerts, status: 'good' },
          { label: 'Critical', value: criticalAlerts, status: criticalAlerts > 0 ? 'critical' : 'good' },
          { label: 'Status', value: isConnected ? 'LIVE' : 'OFFLINE', status: isConnected ? 'good' : 'critical' },
        ]}
      />

      <DashboardSection title="Machine Status" subtitle="Real-time equipment monitoring">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {machines.map(machine => {
            const machineId = machine.sk.split('#')[1];
            const status = getMachineStatus(machineId);
            const machineAlerts = activeAlerts.filter(alert => alert.machineId === machineId);
            const latestTelemetry = (samples as any)[machineId]?.[(samples as any)[machineId].length - 1];
            
            return (
              <MachineStatusCard
                key={machine.sk}
                machine={machine}
                status={status}
                activeAlerts={machineAlerts}
                latestTelemetry={latestTelemetry}
                onViewDetails={handleViewMachineDetails}
              />
            );
          })}
        </div>
      </DashboardSection>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection title="Alert Summary">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <MetricCard title="Critical" value={criticalAlerts} />
            <MetricCard title="Open" value={openAlerts} />
            <MetricCard title="In Progress" value={ackAlerts} />
          </div>

          {criticalAlerts > 0 && (
            <div className="border-t border-slate-700 pt-4">
              <h4 className="text-md font-medium text-red-400 mb-2">Immediate Attention Required</h4>
              <div className="space-y-2">
                {activeAlerts.filter(a => a.severity === 'LATE').slice(0, 3).map(alert => (
                  <div key={alert.alertId} className="flex items-center justify-between p-2 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex-1 cursor-pointer" onClick={() => setSelectedAlert(alert)}>
                      <div className="text-sm font-medium text-white">{alert.summary}</div>
                      <div className="text-xs text-slate-400">
                        {alert.machineId} • {new Date(alert.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                    <AlertActions
                      alert={alert}
                      onAcknowledge={handleAcknowledgeAlert}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DashboardSection>

        <DashboardWidget title="Active Alerts">
          <AlertList
            alerts={activeAlerts}
            onAcknowledge={handleAcknowledgeAlert}
            onSelectAlert={setSelectedAlert}
          />
        </DashboardWidget>
      </div>

      {/* AI Explanation Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <AIExplanationPanel
              alert={selectedAlert}
              userRole="PLANT"
              onClose={() => setSelectedAlert(null)}
            />
          </div>
        </div>
      )}
    </DashboardContentShell>
  );
}